import React, { useRef, useState } from 'react';
import { PhotoCamera } from '@mui/icons-material';
import {
  Typography,
  Avatar,
  Box,
  Container,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { resizeImage, uploadImageToCloudinary } from '../../api/cloudinary';
import { fetchPost } from '../../api/common';
import { Profile } from '@par/shared/types/player';
import { getAvatarSrc, utcToLocalDate } from '../room/utils';
import { ProfileInfoStack } from './ProfileInfoStack';

type PropTypes = {
  onUpdateProfile: (updatedUsername?: string) => void;
  profile: Profile;
  viewProfile: Profile;
};

export const ProfileAvatar = ({
  onUpdateProfile,
  profile,
  viewProfile,
}: PropTypes) => {
  const [dimAvatar, setDimAvatar] = useState(false);
  const fileUploadRef = useRef<HTMLInputElement>(null);

  const theme = useTheme();

  const imageUpload = async (e: any) => {
    e.preventDefault();
    const file = e.target.files[0];
    const cloudinaryRes = await uploadImageToCloudinary(file);
    const res = await fetchPost('/profile/avatar-url', {
      avatarUrl: cloudinaryRes.url,
      avatarPublicId: cloudinaryRes.public_id,
    });
    onUpdateProfile();
  };

  return (
    <>
      <Box>
        <Typography
          component="h1"
          variant="h3"
          paddingBottom={2}
          sx={{
            textShadow:
              theme.palette.mode === 'dark'
                ? `0 0 0px ${theme.palette.primary.main}, 0 0 0px ${theme.palette.primary.main}, 0px 0px 16px ${theme.palette.primary.main}`
                : 'none',
          }}
        >
          {viewProfile?.username}
        </Typography>
      </Box>
      <Box sx={{ position: 'relative', marginBottom: 1 }}>
        <Avatar
          src={getAvatarSrc(
            viewProfile?.avatarPublicId,
            viewProfile?.avatarUrl,
            400,
            400
          )}
          variant="rounded"
          sx={{
            height: '100%',
            width: '100%',
            opacity: dimAvatar ? 0.5 : 1,
          }}
        />
        {viewProfile?.id === profile.id ? (
          <Container
            sx={{
              position: 'absolute',
              top: 0,
              height: '100%',
              opacity: 0,
              '&:hover': { opacity: 1 },
            }}
          >
            <IconButton
              color="primary"
              aria-label="upload picture"
              component="label"
              sx={{ height: '100%', width: '100%' }}
              onMouseEnter={() => setDimAvatar(true)}
              onMouseLeave={() => setDimAvatar(false)}
            >
              <input
                hidden
                accept="image/*"
                type="file"
                ref={fileUploadRef}
                onChange={(e) => imageUpload(e)}
              />
              <PhotoCamera />
            </IconButton>
          </Container>
        ) : null}
      </Box>
      <Box mb={1}>
        <TableContainer component={Paper}>
          <Table size="small" aria-label="simple table">
            <TableBody>
              <TableRow>
                <TableCell>Joined</TableCell>
                <TableCell style={{ textAlign: 'right' }}>
                  {utcToLocalDate(viewProfile.createdAt)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell style={{ textAlign: 'right' }}>
                  {viewProfile.fullName || '--'}
                </TableCell>
              </TableRow>
              <TableRow
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell>Location</TableCell>
                <TableCell style={{ textAlign: 'right' }}>
                  {viewProfile.location || '--'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Pronoun</TableCell>
                <TableCell style={{ textAlign: 'right' }}>
                  {viewProfile.genderPronoun || '--'}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {profile && viewProfile ? (
        <ProfileInfoStack
          onUpdateProfile={onUpdateProfile}
          profile={profile}
          viewProfile={viewProfile}
        ></ProfileInfoStack>
      ) : null}
    </>
  );
};
